import { captureException } from '@sentry/react'
import { THEME } from '@themenu/design-system'
import { useMemo } from 'react'

const THEMES = [THEME.LIGHT, THEME.DARK] as const
const LOCAL_STORAGE_KEY = 'swile_theme'
export type Theme = (typeof THEMES)[number]
type ThemeKey = Theme | 'system'

const setLocalStorageTheme = (theme: ThemeKey) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, theme)
  } catch (error) {
    captureException(error)
  }
}

const getLocalStorageTheme = (): ThemeKey => {
  try {
    return (localStorage.getItem(LOCAL_STORAGE_KEY) as ThemeKey) || 'system'
  } catch (error) {
    captureException(error)
    return 'system'
  }
}

const getSystemTheme = (): Theme => {
  const isDarkSystemTheme = (): boolean => window.matchMedia('(prefers-color-scheme: dark)').matches
  return isDarkSystemTheme() ? 'dark' : 'light'
}

export const useLocalTheme = (): {
  theme: Theme
  getLocalStorageTheme: () => ThemeKey
  setLocalStorageTheme: (value: ThemeKey) => void
  getSystemTheme: () => Theme
} =>
  useMemo(() => {
    const themeQueryParam = new URLSearchParams(location.search).get('theme') as Theme

    if (themeQueryParam && THEMES.includes(themeQueryParam)) {
      setLocalStorageTheme(themeQueryParam)
      return {
        theme: themeQueryParam,
        getLocalStorageTheme,
        setLocalStorageTheme,
        getSystemTheme,
      }
    }

    const localStorageTheme = getLocalStorageTheme()
    return {
      theme: localStorageTheme === 'system' ? getSystemTheme() : localStorageTheme,
      getLocalStorageTheme,
      setLocalStorageTheme,
      getSystemTheme,
    }
  }, [])
